import { NextPlan } from "@mui/icons-material";
import { MeterAccess } from "../pages/NotifierForm/AccountForm/UtilitiesAccountFields/MeterAccessField";
import { NotifierFormState, Section } from "../Sections";
import { assertUnreachable } from "../utils/Functions";

export interface NotifierFormData {
  readonly notifier?: NotifierDetails;
  readonly deceased?: DeceasedDetails;
  readonly executors?: Array<ExecutorFields>;
  readonly accounts?: ReadonlyArray<AccountDetails>;
  readonly documents?: DocumentsDetails;
  readonly submittedSections: NotifierFormState;
  readonly kycPending: boolean;
  readonly submitted: boolean;
}

export interface PersonDetails extends FullDetails {
  readonly id?: string;
  readonly relationshipToDeceased?: Relationship;
  readonly relationshipToDeceasedOther?: string;
  readonly proofOfAddress?: string[];
  readonly dateOfBirth?: string;
  readonly nationality?: string;
  readonly roles?: string[];
  readonly role?: string;
  readonly emailAddressVerified?: boolean;
  readonly phoneNumberVerified?: boolean;
  readonly kycCompleted?: boolean;
  readonly isVulnerable?: boolean;
}

export interface NotifierDetails extends PersonDetails {
  readonly contactTime?: string;
}

export enum Relationship {
  Spouse = "spouse",
  CivilPartner = "civilpartner",
  Partner = "partner",
  Child = "child",
  Grandchild = "grand_child",
  Parent = "parent",
  Grandparent = "grandparent",
  GreatGrandparent = "great_grandparent",
  Sibling = "sibling",
  NephewOrNiece = "nephew_or_niece",
  GreatNephewOrGreatNiece = "great_nephew_or_great_niece",
  UncleOrAunt = "uncle_or_aunt",
  Stepchild = "stepchild",
  Stepmother = "stepmother",
  Stepfather = "stepfather",
  BrotherInLaw = "brother_in_law",
  SisterInLaw = "sister_in_law",
  FatherInLaw = "father_in_law",
  MotherInLaw = "mother_in_law",
  SonInLaw = "son_in_law",
  DaughterInLaw = "daughter_in_law",
  Cousin = "cousin",
  // LegalRepresentative = "legal_representative",
  Other = "other",

  // removed
  RelatedByMarriage = "related_by_marriage",
}

export interface FullDetails {
  readonly title?: Titles;
  readonly firstName?: string;
  readonly surname?: string;
  readonly address?: string;
  readonly city?: string;
  readonly postcode?: string;
  readonly contactNumber?: string;
  readonly email?: string;
}

export enum Titles {
  Mr = "mr",
  Mrs = "mrs",
  Miss = "miss",
  Ms = "ms",
  Dr = "dr",
  Prof = "prof",
  Rev = "rev",
  Sir = "sir",
  Lady = "lady",
  Lord = "lord",
}

export const prettyTitle = (t: Titles | undefined) => {
  return !t
    ? ""
    : {
        [Titles.Mr]: "Mr.",
        [Titles.Mrs]: "Mrs.",
        [Titles.Miss]: "Miss",
        [Titles.Ms]: "Ms.",
        [Titles.Dr]: "Dr.",
        [Titles.Prof]: "Prof.",
        [Titles.Rev]: "Rev.",
        [Titles.Sir]: "Sir",
        [Titles.Lady]: "Lady",
        [Titles.Lord]: "Lord",
      }[t];
};

export interface ExecutorFields extends FullDetails {
  readonly id?: string;
  readonly companyName?: string;
}

export interface DeceasedDetails {
  readonly title?: Titles;
  readonly firstName?: string;
  readonly surname?: string;
  readonly aliases?: string;
  readonly dateOfBirth?: string;
  readonly dateOfDeath?: string;
  readonly maritalStatus?: string;
  readonly idNumber?: string;
  readonly propertyId?: string;
  readonly contactNumber?: string;
  readonly email?: string;
  readonly caseOfDeath?: string;
  readonly deathCertificateNumber?: string;
  readonly regularCreditsFromDWP?: boolean;
}

export interface DocumentsDetails {
  readonly proofOfAddressIds: string[];
  readonly proofOfDeathIds: string[];
  readonly willAndProbateIds: string[];
  readonly grantOfRepresentationIds: string[];
  readonly appliedForGrantOfRepresentationOn?: string;
  readonly tags?: string[];
}

export type DocumentShell = {
  id: string;
  name: string;
  tags?: ReadonlyArray<string>;
};

export type AccountDetails = {
  readonly additionalServicesAccount?: boolean;
  readonly additionalServicesDetails?: {
    type: string;
  };
  readonly id?: string;
  readonly companyId?: string;
  readonly companyType?: CompanyType;
  readonly companyName?: string;
  readonly accountNumber?: string;
  readonly sortCode?: string;
  readonly holderNumber?: string;
  readonly accountType?: AccountType;
  readonly accountTypeOther?: string;
  readonly nextSteps?: AccountNextStep;
  readonly documentIds?: ReadonlyArray<string>;
  readonly documents?: ReadonlyArray<DocumentShell>;
  readonly responsibleId?: string;
  readonly propertyId?: string;
  readonly source?: "express_form";
} & {
  // Utilities
  readonly lastMeterReading?: string; // optional
  readonly meterType?: MeterType;
  readonly lastMeterReadingDate?: string;

  readonly meterAccess?: MeterAccess;
  readonly meterNumber?: string;
  readonly responsibleRejectsArrears?: boolean;
} & {
  // Banks & Insurances
  //readonly sortCode?: string;
  //readonly holderNumber?: string;
  //readonly accountType?: string;
  readonly needsTaxCertificate?: boolean;
  readonly vehicleMake?: string;
  readonly vehicleModel?: string;
  readonly vehicleRegistrationNumber?: string;
  readonly companyName?: string;
  readonly accountName?: string;
  readonly holderOrUser?: "holder" | "user" | "both";
} & {
  // Banks
  // readonly useToPayFuneralExpenses?: YesNo;
} & {
  // Insurances
  readonly jointClaim?: YesNo;
  readonly refundPaymentMethod?: RefundPaymentMethod;
  readonly refundBankAccountHolderName?: string;
  readonly refundBankAccountNumber?: string;
  readonly refundBankAccountSortCode?: string;
  readonly refundChequePayableTo?: "notifier" | "responsible";
  readonly paymentBankAccountHolderName?: string;
  readonly paymentBankAccountNumber?: string;
  readonly paymentBankAccountSortCode?: string;
} & {
  ///Social Media
  readonly emailAddress?: string;
  readonly phoneNumber?: string;
  readonly profileUrl?: string;
};

export enum RefundPaymentMethod {
  BankTransferToAssociatedAccount = "bank_transfer_to_associated_account",
  BankTransferToNewAccount = "bank_transfer_to_new_account",
  Cheque = "cheque",
}

export enum MeterType {
  SmartMeter = "smart_meter",
  Credit = "credit",
  PayAsYouGo = "pay_as_you_go",
  Unknown = "unknown",
}

export enum YesNo {
  Yes = "YES",
  No = "NO",
}

export enum ContactMethods {
  Email = "EMAIL",
  Post = "POST",
  Phone = "PHONE",
  Either = "EITHER",
}

export enum AccountNextStep {
  Cancel = "CANCEL",
  MoveToPayAsYouGo = "MOVE_TO_PAY_AS_YOU_GO",
  MoveToAnotherService = "MOVE_TO_ANOTHER_SERVICE",
  RequestAccountBalance = "REQUEST_ACCOUNT_BALANCE",
  UpdateAccountHolder = "UPDATE_ACCOUNT_HOLDER",
  RequestInformation = "REQUEST_INFORMATION",
}

export enum CompanyType {
  Banking = "banking",
  Betting = "betting",
  Broadband = "broadband",
  BuildingSociety = "building_society",
  Charities = "charities",
  CreditCards = "credit_cards",
  Ecommerce = "ecommerce",
  Email = "email",
  Energy = "energy",
  Insurance = "insurance",
  Investments = "investments",
  Loans = "loans",
  LoyaltyCards = "loyalty_cards",
  Magazines = "magazines",
  Mobile = "mobile",
  Mortgage = "mortgage",
  Other = "other",
  Pension = "pension",
  SaaS = "saas",
  Satellite = "satellite",
  SocialMedia = "social_media",
  Streaming = "streaming",
  Subscriptions = "subscriptions",
  Telecoms = "telecoms",
  TV = "tv",
  Water = "water",
  affiliatePartner = "affiliate_partner",
}

export type FuneralDirectorFields = {
  readonly firstName?: string;
  readonly surname?: string;
  readonly company?: string;
  readonly address?: string;
  readonly contactNumber?: string;
  readonly email?: string;
  readonly sortCode?: string;
};

export type AdditionalHelpDetails = {
  requestMissingAssetSearch?: YesNo;
  requestProbateSolicitor?: YesNo;
  requestHomeRemoval?: YesNo;
  requestPropertyValuationService?: YesNo;
  requestProbateHouseInsurance?: YesNo;
  requestExecutorInsurance?: YesNo;
  requestProbateAndFuneralBridgingLoans?: YesNo;
  // readonly requestFinancialGuidance?: YesNo;
  // readonly requestFuneralDirector?: YesNo;
  // readonly requestHelpDealingWithDebt?: YesNo;
  // readonly requestHelpDealingWithFamilyConflict?: YesNo;
  // readonly requestLocalGriefSupport?: YesNo;
  // readonly requestProbateBridgingLoans?: YesNo;
};
// ui info, backend field
export const additionalServices: ReadonlyArray<[string, keyof AdditionalHelpDetails]> = [
  ["Check for missing assets", "requestMissingAssetSearch"],
  ["Probate and Estate Admin", "requestProbateSolicitor"],
  ["House clearance", "requestHomeRemoval"],
  ["Property valuation & sale", "requestPropertyValuationService"],
  ["Probate House Insurance", "requestProbateHouseInsurance"],
  ["Executor Insurance", "requestExecutorInsurance"],
  ["Probate and Funeral bridging loans", "requestProbateAndFuneralBridgingLoans"],

  // ["Probate bridging loans", "requestProbateBridgingLoans"],
  // ["Arranging the funeral", "requestFuneralDirector"],
  // ["Dealing with family conflicts", "requestHelpDealingWithFamilyConflict"],
  // ["Dealing with debt left behind", "requestHelpDealingWithDebt"],
  // ["Accessing grief support", "requestLocalGriefSupport"],
  // ["Seeking financial guidance", "requestFinancialGuidance"],
];

// Helper functions and objects.
export const fullDetailsRequiredFields: ReadonlyArray<keyof FullDetails> = [
  "firstName",
  "surname",
  "address",
  "postcode",
  "contactNumber",
  "email",
  "title",
  "city",
];

export const readableRelationship = (r?: Relationship, fallback?: string): string => {
  switch (r) {
    case Relationship.Spouse:
      return "Spouse (Husband or Wife)";
    case Relationship.CivilPartner:
      return "Civil Partner";
    case Relationship.Partner:
      return "Partner";
    case Relationship.Child:
      return "Child";
    case Relationship.Grandchild:
      return "Grandchild";
    case Relationship.Parent:
      return "Parent";
    case Relationship.Grandparent:
      return "Grandparent";
    case Relationship.GreatGrandparent:
      return "Great-Grandparent";
    case Relationship.Sibling:
      return "Sibling";
    case Relationship.NephewOrNiece:
      return "Nephew or Niece";
    case Relationship.GreatNephewOrGreatNiece:
      return "Great-nephew or Great-niece";
    case Relationship.UncleOrAunt:
      return "Uncle or Aunt";
    case Relationship.Stepchild:
      return "Stepchild";
    case Relationship.Stepmother:
      return "Stepmother";
    case Relationship.Stepfather:
      return "Stepfather";
    case Relationship.BrotherInLaw:
      return "Brother-in-law";
    case Relationship.SisterInLaw:
      return "Sister-in-law";
    case Relationship.FatherInLaw:
      return "Father-in-law";
    case Relationship.MotherInLaw:
      return "Mother-in-law";
    case Relationship.SonInLaw:
      return "Son-in-law";
    case Relationship.DaughterInLaw:
      return "Daughter-in-law";
    case Relationship.Cousin:
      return "Cousin";
    // case Relationship.LegalRepresentative:
    //   return "Solicitor/Legal Representative";
    case Relationship.Other:
      return "Other";

    // removed
    case Relationship.RelatedByMarriage:
      return "Related By Marriage (in-law)";
    case undefined:
      return fallback || "Unknown";
    case null:
      return fallback || "Unknown";
  }
  assertUnreachable(r);
};

export const readableContactMethod = (c?: ContactMethods, fallback?: string): string => {
  switch (c) {
    case ContactMethods.Email:
      return "Email";
    case ContactMethods.Post:
      return "Post";
    case ContactMethods.Phone:
      return "Phone";
    case ContactMethods.Either:
      return "I don't mind";
    case undefined:
    case null:
      return fallback || "Unknown";
  }
  assertUnreachable(c);
};

export const readableCompanyType = (t?: CompanyType, fallback?: string): string => {
  switch (t) {
    case CompanyType.Banking:
      return "Banking";
    case CompanyType.Betting:
      return "Betting";
    case CompanyType.Broadband:
      return "Broadband";
    case CompanyType.BuildingSociety:
      return "Building Society";
    case CompanyType.Charities:
      return "Charities";
    case CompanyType.CreditCards:
      return "Credit cards";
    case CompanyType.Ecommerce:
      return "E-commerce";
    case CompanyType.Email:
      return "Email";
    case CompanyType.Energy:
      return "Energy";
    case CompanyType.Insurance:
      return "Insurance";
    case CompanyType.Investments:
      return "Investments";
    case CompanyType.Loans:
      return "Loans";
    case CompanyType.LoyaltyCards:
      return "Loyalty cards";
    case CompanyType.Magazines:
      return "Magazines";
    case CompanyType.Mobile:
      return "Mobile";
    case CompanyType.Mortgage:
      return "Mortgage";
    case CompanyType.Pension:
      return "Pension";
    case CompanyType.SaaS:
      return "SaaS";
    case CompanyType.Satellite:
      return "Satellite";
    case CompanyType.SocialMedia:
      return "Social media";
    case CompanyType.Streaming:
      return "Streaming";
    case CompanyType.Subscriptions:
      return "Subscriptions";
    case CompanyType.Telecoms:
      return "Telecommunications";
    case CompanyType.TV:
      return "Television";
    case CompanyType.Water:
      return "Water";
    case CompanyType.Other:
      return "Other";
    case CompanyType.affiliatePartner:
      return "Partner";
    case undefined:
    case null:
      return fallback || "Unknown";
  }
  assertUnreachable(t);
};

export const readableAccountNextSteps = (
  a?: AccountNextStep | null,
  type?: CompanyType
): string => {
  switch (a) {
    case AccountNextStep.Cancel:
      return type === CompanyType.Insurance ? "Cancel policy" : "Close account";
    case AccountNextStep.MoveToAnotherService:
      return type === CompanyType.Mobile
        ? "Move to a new mobile network"
        : "Move to another service";
    case AccountNextStep.MoveToPayAsYouGo:
      return "Move to Pay as you go";
    case AccountNextStep.RequestAccountBalance:
      return `Request ${
        type === CompanyType.Insurance ? "policy" : "account"
      } balance at the date of death`;
    case AccountNextStep.UpdateAccountHolder:
      return type === CompanyType.Insurance
        ? "Update policy holder"
        : "Update primary account holder";
    case AccountNextStep.RequestInformation:
      return "Request Information";
    case undefined:
    case null:
      return "Unknown";
  }
  assertUnreachable(a);
};

export enum AccountType {
  CurrentAccount = "current_account",
  SavingsAccount = "savings_account",
  CashISA = "cash_isa",

  RetirementAccount = "retirement_account",
  Annuities = "annuities",
  WorkplacePension = "workplace_pension",

  Remortgage = "remortgage",
  FirstTimeBuyer = "first_time_buyer",
  BuyToLet = "buy_to_let",

  Vehicle = "vehicle",
  BreakdownCover = "breakdown_cover",
  Home = "home",
  Health = "health",
  Travel = "travel",
  Business = "business",
  Life = "life",
  Disability = "disability",
  Pet = "pet",

  PayAsYouGo = "pay_as_you_go",
  Contract = "contract",

  Personal = "personal",
  PersonalAndBusiness = "personal_and_business",

  PersonalAccidentInsurance = "personal_accident_insurance",
  PrivateMedicalInsurance = "private_medical_insurance",
  HospitalCashPlan = "hospital_cash_plan",
  CancerCover = "cancer_cover",

  Other = "other",
  Unknown = "unknown",
}

export const readableAccountType = (type?: AccountType, other?: string) => {
  switch (type) {
    case AccountType.CurrentAccount:
      return `Current account`;
    case AccountType.SavingsAccount:
      return "Savings account";
    case AccountType.CashISA:
      return "Cash ISA";

    case AccountType.RetirementAccount:
      return "Retirement account";
    case AccountType.Annuities:
      return "Annuities";
    case AccountType.WorkplacePension:
      return "Workplace pension";

    case AccountType.Remortgage:
      return "Remortgage";
    case AccountType.FirstTimeBuyer:
      return "First-time buyer";
    case AccountType.BuyToLet:
      return "Buy-to-let";

    case AccountType.Vehicle:
      return "Vehicle";
    case AccountType.BreakdownCover:
      return "Breakdown cover";
    case AccountType.Home:
      return "Home";
    case AccountType.Health:
      return "Health";
    case AccountType.Travel:
      return "Travel";
    case AccountType.Business:
      return "Business";
    case AccountType.Life:
      return "Life";
    case AccountType.Disability:
      return "Disability";
    case AccountType.Pet:
      return "Pet";

    case AccountType.PayAsYouGo:
      return "Pay as you go";

    case AccountType.Contract:
      return "Contract";

    case AccountType.Personal:
      return "Personal";

    case AccountType.PersonalAndBusiness:
      return "Personal and business accounts";

    case AccountType.PersonalAccidentInsurance:
      return "Personal accident insurance";
    case AccountType.PrivateMedicalInsurance:
      return "Private medical insurance";
    case AccountType.HospitalCashPlan:
      return "Hospital cash plan";
    case AccountType.CancerCover:
      return "Cancer cover";

    case AccountType.Other:
      return other || "Other";
    case AccountType.Unknown:
    case undefined:
    case null:
      return "Unknown";
  }
  assertUnreachable(type);
};

export const readableMeterType = (meterType?: MeterType) => {
  switch (meterType) {
    case MeterType.SmartMeter:
      return "Smart meter";

    case MeterType.Credit:
      return "Credit";

    case MeterType.PayAsYouGo:
      return "Pay as you go";

    case MeterType.Unknown:
      return "Not known";

    case undefined:
    case null:
      return "Unknown";
  }
  assertUnreachable(meterType);
};

export const roleOptions = [
  { value: "sole_executor", label: "The Sole Executor named on the will " },
  { value: "executor", label: "An Executor named on the will, alongside others" },
  { value: "administrator", label: "An Administrator (there is no will)" },
  { value: "delegated_notifier", label: "Notifying on behalf of someone else" },
  { value: "solicitor", label: "A Solicitor" },
];
